import { Controller } from "@hotwired/stimulus";

// Nested form du builder de quiz enseignant (QZ-402).
// - addQuestion : clone le <template> du type demandé (index __IDX__ remplacé
//   par un compteur croissant — jamais de collision avec les index existants).
// - removeQuestion : question persistée → _destroy=1 + carte masquée ;
//   nouvelle → carte retirée du DOM.
// - moveUp/moveDown : réordonne les cartes ; les inputs position sont
//   recalculés à la soumission (ordre DOM = ordre du quiz).
// - addChoice/removeChoice : lignes de choix avec clés stables c1, c2, …
//   (les clés des choix existants ne changent JAMAIS — les réponses les
//   référencent).
export default class extends Controller {
  static targets = ["container", "template", "empty", "question", "number",
                    "position", "destroyFlag", "choiceRow",
                    "bankQuery", "bankResults", "refTemplate"];
  static values = { bankUrl: String };

  connect() {
    this.counter = 1000; // au-delà de tout index rendu côté serveur
    this.renumber();
    this.element.addEventListener("submit", () => this.reindexPositions());
  }

  addQuestion(event) {
    const type = event.params.type;
    const template = this.templateTargets.find((t) => t.dataset.type === type);
    if (!template) return;

    const html = template.innerHTML.replaceAll("__IDX__", this.counter++);
    this.containerTarget.insertAdjacentHTML("beforeend", html);
    this.renumber();
    const added = this.questionTargets[this.questionTargets.length - 1];
    added.querySelector("textarea")?.focus();
  }

  removeQuestion(event) {
    const card = event.target.closest("[data-quiz-builder-target~='question']");
    const idInput = card.querySelector("input[name$='[id]']");
    if (idInput && idInput.value) {
      card.querySelector("input[name$='[_destroy]']").value = "1";
      card.hidden = true;
    } else {
      card.remove();
    }
    this.renumber();
  }

  moveUp(event) {
    const card = event.target.closest("[data-quiz-builder-target~='question']");
    const prev = this.previousVisible(card);
    if (prev) card.parentNode.insertBefore(card, prev);
    this.renumber();
  }

  moveDown(event) {
    const card = event.target.closest("[data-quiz-builder-target~='question']");
    const next = this.nextVisible(card);
    if (next) card.parentNode.insertBefore(next, card);
    this.renumber();
  }

  addChoice(event) {
    const zone = event.target.closest("[data-quiz-builder-target~='choices']");
    const rows = zone.querySelectorAll("[data-quiz-builder-target~='choiceRow']");
    const last = rows[rows.length - 1];
    if (!last) return;

    const clone = last.cloneNode(true);
    // Clé stable suivante : max des cN existants + 1 (les clés des réponses
    // déjà enregistrées ne bougent jamais).
    const maxKey = Math.max(...Array.from(rows).map((r) => {
      const key = r.querySelector("input[data-role='choice-key']")?.value || "c0";
      return parseInt(key.replace(/\D/g, ""), 10) || 0;
    }));
    clone.querySelector("input[data-role='choice-key']").value = `c${maxKey + 1}`;
    const text = clone.querySelector("input[type='text']");
    text.value = "";
    const check = clone.querySelector("input[type='checkbox'], input[type='radio']");
    if (check) {
      check.checked = false;
      check.value = `c${maxKey + 1}`;
    }
    last.after(clone);
  }

  removeChoice(event) {
    const zone = event.target.closest("[data-quiz-builder-target~='choices']");
    const rows = zone.querySelectorAll("[data-quiz-builder-target~='choiceRow']");
    if (rows.length <= 2) return; // jamais moins de 2 choix

    event.target.closest("[data-quiz-builder-target~='choiceRow']").remove();
  }

  // ---- Lignes répétables génériques (ordering/matching/cloze — QZ-1107) ------
  // Une zone `[data-repeat-zone]` contient des `[data-repeat-row]`. addRow clone
  // la dernière ligne et vide ses champs ; les clés internes (i1/l1/r1) sont
  // régénérées par position CÔTÉ SERVEUR (mapper). Les trous cloze portent une
  // clé explicite (data-role='row-key') auto-incrémentée selon data-repeat-key-prefix.
  addRow(event) {
    const zone = event.target.closest("[data-repeat-zone]");
    const rows = zone.querySelectorAll("[data-repeat-row]");
    const last = rows[rows.length - 1];
    if (!last) return;

    const clone = last.cloneNode(true);
    clone.querySelectorAll("input, textarea").forEach((el) => { el.value = ""; });
    const keyInput = clone.querySelector("[data-role='row-key']");
    if (keyInput) {
      const prefix = zone.dataset.repeatKeyPrefix || "b";
      const maxN = Math.max(0, ...Array.from(rows).map((r) => {
        const value = r.querySelector("[data-role='row-key']")?.value || "";
        return parseInt(value.replace(/\D/g, ""), 10) || 0;
      }));
      keyInput.value = `${prefix}${maxN + 1}`;
    }
    last.after(clone);
  }

  removeRow(event) {
    const zone = event.target.closest("[data-repeat-zone]");
    const min = parseInt(zone.dataset.repeatMin || "1", 10);
    if (zone.querySelectorAll("[data-repeat-row]").length <= min) return;

    event.target.closest("[data-repeat-row]").remove();
  }

  // ---- Picker banque (QZ-902) ---------------------------------------------

  searchBank() {
    clearTimeout(this.bankTimer);
    this.bankTimer = setTimeout(() => this.fetchBank(), 300);
  }

  async fetchBank() {
    if (!this.bankUrlValue) return;
    const query = this.bankQueryTarget.value.trim();
    const subject = this.element.querySelector("select[name='quiz[subject_key_id]']")?.value || "";
    const url = `${this.bankUrlValue}?q=${encodeURIComponent(query)}&subject_key_id=${subject}`;
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      if (!response.ok) return;
      const body = await response.json();
      this.renderBankResults(body.data || []);
    } catch { /* réseau : le picker reste silencieux */ }
  }

  renderBankResults(rows) {
    this.bankResultsTarget.innerHTML = "";
    rows.forEach((row) => {
      const alreadyAdded = !!this.element.querySelector(`input[name$='[id]'][value='${row.id}']`);
      const item = document.createElement("button");
      item.type = "button";
      item.className = "text-left ds-card p-2 text-sm flex items-center justify-between gap-2 hover:bg-gray-50";
      item.disabled = alreadyAdded;
      const label = document.createElement("span");
      label.textContent = row.statement;
      const meta = document.createElement("span");
      meta.className = "ds-chip shrink-0";
      meta.textContent = alreadyAdded ? "✓" : `${row.question_type} · ${row.usage_count}`;
      item.append(label, meta);
      item.addEventListener("click", () => this.addReference(row));
      this.bankResultsTarget.appendChild(item);
    });
  }

  // Échappe les valeurs serveur avant injection HTML (anti-XSS stocké : un
  // énoncé de question peut contenir < > & " ').
  escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }

  addReference(row) {
    const html = this.refTemplateTarget.innerHTML
      .replaceAll("__IDX__", this.counter++)
      .replaceAll("__QID__", this.escapeHtml(row.id))
      .replaceAll("__QTYPE__", this.escapeHtml(row.question_type))
      .replaceAll("__TYPE_LABEL__", this.escapeHtml(row.question_type))
      .replaceAll("__STATEMENT__", this.escapeHtml(row.statement))
      .replaceAll("__USAGE__", this.escapeHtml(row.usage_count))
      .replaceAll("__POINTS__", this.escapeHtml(row.points));
    this.containerTarget.insertAdjacentHTML("beforeend", html);
    this.renumber();
    this.fetchBank();
  }

  // Positions = ordre DOM des cartes visibles au moment de la soumission.
  reindexPositions() {
    this.visibleQuestions().forEach((card, index) => {
      const input = card.querySelector("input[name$='[position]']");
      if (input) input.value = index;
    });
  }

  renumber() {
    this.visibleQuestions().forEach((card, index) => {
      const label = card.querySelector("[data-quiz-builder-target~='number']");
      if (label) label.textContent = `#${index + 1}`;
    });
    if (this.hasEmptyTarget) this.emptyTarget.hidden = this.visibleQuestions().length > 0;
  }

  visibleQuestions() {
    return this.questionTargets.filter((card) => !card.hidden);
  }

  previousVisible(card) {
    let node = card.previousElementSibling;
    while (node && node.hidden) node = node.previousElementSibling;
    return node;
  }

  nextVisible(card) {
    let node = card.nextElementSibling;
    while (node && node.hidden) node = node.nextElementSibling;
    return node;
  }
}
